import { useState } from "react";
import { Card, Flex, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { Bar } from "@ant-design/plots";
import { nombreDePeriodo, type DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";
import type { PerformancePersonal, PerformanceSupervisor } from "../../api/dashboard.api";

const { Text } = Typography;

const CARD_HEAD   = 38;
const PAD_V       = 18;
const MIN_CHART_H = 80;



/** Qué métrica compara el gráfico del supervisor. `respuesta` todavía no tiene dato en la API. */
type Metrica = "comprado" | "conversion" | "activos" | "respuesta";
const METRICAS: { value: Metrica; label: string }[] = [
  { value: "comprado", label: "Cerrados" },
  { value: "conversion", label: "Conversión" },
  { value: "activos", label: "Activos" },
  { value: "respuesta", label: "Respuesta" },
];

type Highlight = "me" | "selected" | "other";
type OperatorRow = {
  operatorId: number;
  name: string;
  comprado: number;
  activos: number;
  conversion: number;
};



/** El promedio del equipo y la conversión vienen de la API; acá sólo se los formatea. */
const redondear = (n: number | null) => (n === null ? "—" : Math.round(n * 10) / 10);
const pct = (n: number | null) => (n === null ? "—" : Math.round(n * 100));

const HIGHLIGHT_COLORS: Record<Highlight, string> = {
  me:       "#15744b",
  selected: "#1677ff",
  other:    "rgba(0,0,0,0.10)",
};

// ── KPI tile (personal scorecard) ────────────────────────────────────────────

/** Card sin dato: el tiempo de respuesta sale de conversation-service, que todavía no lo expone. */
const KpiTileSinDatos = ({ label }: { label: string }) => (
  <div style={{ flex: 1, padding: "9px 11px 8px", borderRadius: 8, background: "#fafafa", display: "flex", flexDirection: "column", gap: 1 }}>
    <Text style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.3)", letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.4 }}>
      {label}
    </Text>
    <Text strong style={{ fontSize: 21, lineHeight: 1.2, color: "rgba(0,0,0,0.25)" }}>—</Text>
    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.3 }}>Sin datos para mostrar</Text>
  </div>
);

const RankBadge = ({ pos, total }: { pos: number; total: number }) => {
  const color = pos === 1 ? "#15744b" : pos <= Math.ceil(total / 2) ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.25)";
  return (
    <Text style={{ fontSize: 11, color, fontWeight: pos === 1 ? 700 : 400 }}>
      #{pos} de {total}
    </Text>
  );
};

const KpiTile = ({
  label, value, sub, rank: pos, total,
}: {
  label: string; value: React.ReactNode; sub: string; rank: number; total: number;
}) => (
  <div style={{ flex: 1, padding: "9px 11px 8px", borderRadius: 8, background: "#fafafa", display: "flex", flexDirection: "column", gap: 1 }}>
    <Text style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.3)", letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.4 }}>
      {label}
    </Text>
    <Flex align="baseline" justify="space-between">
      <Text strong style={{ fontSize: 21, lineHeight: 1.2, color: "rgba(0,0,0,0.85)" }}>{value}</Text>
      <RankBadge pos={pos} total={total} />
    </Flex>
    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.3 }}>{sub}</Text>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

type Datos = PerformancePersonal | PerformanceSupervisor;

export const PerformanceOperadoresChip = ({
  height = 240,
  period = "day",
  selectedOperatorIds = [],
  myOperatorId = null,
  data: metrics,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: {
  height?: number;
  period?: DashboardPeriod;
  selectedOperatorIds?: string[];
  /** El userId del operador logueado. Es lo que decide qué fila es "yo". */
  myOperatorId?: number | null;
} & WidgetDataProps<Datos>) => {
  const [metrica, setMetrica] = useState<Metrica>("comprado");

  // El modo lo decide la API, no el front: en personal **no llegan** datos individuales de otros
  // operadores. Ver `features/Admin/home/home.md`, "Principio de privacidad".
  const personal = metrics?.modo === "personal" ? metrics : null;
  const supervisor = metrics?.modo === "supervisor" ? metrics : null;

  // `respuesta` no está entre los tiles: sale de conversation-service, que todavía no expone
  // métricas. Se omite en vez de mostrarse en cero, que se leería como "contestamos al instante".
  const data: OperatorRow[] = [...(supervisor?.operadores ?? [])]
    .map((o) => ({
      operatorId: o.operatorId,
      name: o.nombre,
      comprado: o.comprado,
      activos: o.activos,
      conversion: Math.round((o.conversion ?? 0) * 100),
    }))
    .sort((a, b) => b.comprado - a.comprado);
  const hayDatos = personal ? personal.tieneDatosPropios : data.length > 0;
  const status = estadoDeWidget(estado, error, nuncaCalculado, hayDatos, recargar);
  const esYo = (r: OperatorRow) => myOperatorId != null && r.operatorId === myOperatorId;
  const myIdStr = myOperatorId != null ? String(myOperatorId) : null;
  const seleccionados = new Set(
    selectedOperatorIds.filter((id) => id !== myIdStr).map(Number).filter(Number.isFinite),
  );

  // Supervisor mode = hay algún operador puntual elegido además de (o en vez de) uno mismo.
  const isSupervisorMode = selectedOperatorIds.some((id) => id !== myIdStr);
  // Con todo destildado no hay a quién mostrar: este widget no tiene un modo "todos, sin
  // identificar" — es la contracara deliberada de D15, que nunca mezcla el detalle propio con el
  // de otros de forma anónima.
  const sinSeleccion = selectedOperatorIds.length === 0;

  const porNombre = new Map(data.map((r) => [r.name, r]));
  const getHighlight = (name: string): Highlight => {
    const r = porNombre.get(name);
    if (!r) return "other";
    if (esYo(r)) return "me";
    if (seleccionados.has(r.operatorId)) return "selected";
    return "other";
  };

  const bodyH  = height - CARD_HEAD;
  const chartH = Math.max(MIN_CHART_H, bodyH - PAD_V);

  // ── Sin selección ───────────────────────────────────────────────────────────

  if (sinSeleccion) {
    return (
      <Card
        size="small"
        style={{ height }}
        styles={{ body: { padding: "10px 12px 12px", height: bodyH, display: "flex", alignItems: "center", justifyContent: "center" } }}
        title={
          <Flex align="center" gap={8}>
            <TeamOutlined style={{ fontSize: 14 }} />
            <span style={{ fontWeight: 600 }}>Performance del operador</span>
          </Flex>
        }
      >
        <Flex vertical align="center" gap={8}>
          <TeamOutlined style={{ fontSize: 32, color: "rgba(0,0,0,0.12)" }} />
          <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
            Es necesario seleccionar uno o más operadores para ver esta métrica.
          </Text>
        </Flex>
      </Card>
    );
  }

  // ── Personal view (operador regular) ───────────────────────────────────────
  // Shows my KPIs + anonymous rank within team. No individual names disclosed.

  if (!isSupervisorMode) {
    const total = personal?.total ?? 0;
    const sinDatos = status.tipo !== "ok" || !personal?.tieneDatosPropios;

    return (
      <Card
        size="small"
        style={{ height }}
        styles={{ body: { padding: "10px 12px 12px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "space-between" } }}
        title={
          <Flex align="center" gap={8}>
            <UserOutlined style={{ fontSize: 14 }} />
            <span style={{ fontWeight: 600 }}>Performance</span>
            <Tooltip
              title="Tus indicadores clave del período y tu posición relativa en el equipo, sin revelar los datos individuales de otros operadores."
              trigger="click"
              color="#fff"
              overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              overlayStyle={{ maxWidth: 260 }}
            >
              <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </Flex>
        }
        extra={<Text type="secondary" style={{ fontSize: 11 }}>{nombreDePeriodo(period)}</Text>}
      >
        {sinDatos ? (
          <WidgetPlaceholder
            status={status.tipo !== "ok" ? status : { tipo: "vacio" }}
            height={bodyH - 20}
          />
        ) : (
          <Flex vertical gap={6}>
            <Text strong style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>Mi performance</Text>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <KpiTile
              label="Cerrados"
              value={personal!.yo.comprado}
              sub={`prom. equipo: ${redondear(personal!.equipo.comprado)}`}
              rank={personal!.ranking.comprado ?? 0}
              total={total}
            />
            <KpiTile
              label="Conversión"
              value={`${pct(personal!.yo.conversion)}%`}
              sub={`prom. equipo: ${pct(personal!.equipo.conversion)}%`}
              rank={personal!.ranking.conversion ?? 0}
              total={total}
            />
            <KpiTile
              label="Activos"
              value={personal!.yo.activos}
              sub={`prom. equipo: ${redondear(personal!.equipo.activos)}`}
              rank={personal!.ranking.activos ?? 0}
              total={total}
            />
            <KpiTileSinDatos label="Respuesta" />
          </div>
          </Flex>
        )}
      </Card>
    );
  }

  // ── Supervisor view ─────────────────────────────────────────────────────────
  // Full named comparison: me (green, sólo si me elegí) + operadores elegidos (azul) + resto (gris)

  const includeMine    = myIdStr != null && selectedOperatorIds.includes(myIdStr);
  const supervisorData = data.filter(
    (r) => (includeMine && esYo(r)) || seleccionados.has(r.operatorId),
  );
  const campo = metrica === "respuesta" ? "comprado" : metrica;
  supervisorData.sort((a, b) => b[campo] - a[campo]);
  const chipsH = 26;
  const graficoH = chartH - chipsH;
  /** Los nombres de los seleccionados, para la leyenda de la cabecera. */
  const selectedNames = data.filter((r) => seleccionados.has(r.operatorId)).map((r) => r.name);

  const comparisonExtra = (() => {
    if (includeMine && selectedNames.length === 0) return null;
    if (includeMine && selectedNames.length === 1) return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        <Text strong style={{ color: "#15744b", fontSize: 11 }}>Yo</Text>
        {" vs "}
        <Text strong style={{ color: "#1677ff", fontSize: 11 }}>{selectedNames[0]}</Text>
      </Text>
    );
    if (includeMine) return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        <Text strong style={{ color: "#15744b", fontSize: 11 }}>Yo</Text>
        {" vs "}
        <Text strong style={{ color: "#1677ff", fontSize: 11 }}>{selectedNames.length} ops</Text>
      </Text>
    );
    if (selectedNames.length === 2) return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        <Text strong style={{ color: "#1677ff", fontSize: 11 }}>{selectedNames[0]}</Text>
        {" vs "}
        <Text strong style={{ color: "#1677ff", fontSize: 11 }}>{selectedNames[1]}</Text>
      </Text>
    );
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        <Text strong style={{ color: "#1677ff", fontSize: 11 }}>{selectedNames.length} operadores</Text>
      </Text>
    );
  })();

  return (
    <Card
      size="small"
      style={{ height }}
      styles={{ body: { padding: "8px 12px 10px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={
        <Flex align="center" gap={8}>
          <TeamOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Performance</span>
          <Tooltip
            title="Presupuestos cerrados por cada operador seleccionado en el período. Comparalos para detectar quién rinde por encima o por debajo del promedio. Verde = vos · Azul = seleccionados."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 280 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={comparisonExtra}
    >
      <Flex gap={6} style={{ marginBottom: 6, flexShrink: 0 }}>
        {METRICAS.map((m) => (
          <Tag.CheckableTag
            key={m.value}
            checked={metrica === m.value}
            onChange={() => setMetrica(m.value)}
            style={{ margin: 0, fontSize: 11, lineHeight: "18px", padding: "0 8px", border: "1px solid #d9d9d9", borderRadius: 10 }}
          >
            {m.label}
          </Tag.CheckableTag>
        ))}
      </Flex>
      <div style={{ height: graficoH, overflow: "hidden" }}>
        {status.tipo !== "ok" ? (
          <WidgetPlaceholder status={status} height={graficoH} />
        ) : metrica === "respuesta" ? (
          <Flex align="center" justify="center" style={{ height: graficoH }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Sin datos para mostrar</Text>
          </Flex>
        ) : (
        <Bar
          data={supervisorData}
          xField={campo}
          yField="name"
          seriesField="name"
          color={({ name }: any) => HIGHLIGHT_COLORS[getHighlight(name)]}
          barStyle={({ name }: any) => {
            const h = getHighlight(name);
            return {
              fillOpacity: h === "other" ? 0.6 : 0.2,
              stroke:      HIGHLIGHT_COLORS[h],
              lineWidth:   h === "other" ? 0 : 1.5,
            };
          }}
          autoFit
          barWidthRatio={0.55}
          xAxis={{
            // Conversión es un porcentaje: de 0 a 100. Lo demás son cantidades y la escala se ajusta sola.
            min: 0,
            ...(metrica === "conversion" ? { max: 100 } : {}),
            label: {
              style: { fontSize: 10, fill: "rgba(0,0,0,0.35)" },
              formatter: (v: string) => (metrica === "conversion" ? `${v}%` : v),
            },
            grid: { line: { style: { stroke: "#f5f5f5" } } },
            line: null, tickLine: null,
          }}
          yAxis={{
            label: {
              style: { fontSize: 11 },
              formatter: (name: string) => {
                const texto = getHighlight(name) === "me" ? `${name} (yo)` : name;
                return texto.length > 22 ? `${texto.slice(0, 21)}…` : texto;
              },
            },
            line: null, tickLine: null,
          }}
          tooltip={{
            formatter: (d: any) => ({
              name:  getHighlight(d.name) === "me" ? `${d.name} (yo)` : d.name,
              value: `${d.comprado} cerrados · ${d.conversion}% conv. · ${d.activos} activos`,
            }),
            domStyles: {
              "g2-tooltip": { background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "none", borderRadius: "6px", padding: "8px 10px", color: "rgba(0,0,0,0.85)", fontSize: "12px" },
            },
          }}
          legend={false}
          padding={[4, 16, 20, 130]}
          animation={false}
        />
        )}
      </div>
    </Card>
  );
};
